import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import getSimulationHealthCheck from 'redux/actions/Common/getSimulationHealthCheck';

// Requirement 2, Phase 6 — platform-wide snapshot, reusing the same sweep the
// scheduled job (Phase 4) already runs: how many simulations are live/
// stale/orphaned/stopped right now, and which LLM provider they're on.
const LIVE_STATUS_ORDER = ['live', 'stale', 'orphaned', 'stopped'];
const LIVE_STATUS_COLOR = {
  live: 'success',
  stale: 'warning',
  orphaned: 'error',
  stopped: 'default',
};

const SimulationHealthCard = () => {
  const dispatch = useDispatch();
  const [result, setResult] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(null);

  const runCheck = () => {
    setFetching(true);
    setError(null);
    dispatch(
      getSimulationHealthCheck(
        (data) => {
          setResult(data);
          setFetching(false);
        },
        (message) => {
          setError(message);
          setFetching(false);
        },
      ),
    );
  };

  // Runs once on mount — a manual refresh afterward is the Button below, not
  // a polling loop (this already piggybacks on the scheduled sweep's own
  // cadence for anyone who doesn't open this page).
  useEffect(() => {
    runCheck();
  }, []);

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
      <Box
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
        gap={1}
        flexWrap={'wrap'}
      >
        <Box display={'flex'} alignItems={'center'} gap={1} flexWrap={'wrap'}>
          <Typography variant={'subtitle2'} fontWeight={'bold'}>
            Platform health
          </Typography>
          {fetching ? (
            <CircularProgress size={14} />
          ) : result ? (
            <>
              <Typography variant={'caption'} color={'text.secondary'}>
                {result.simulationsChecked} simulation
                {result.simulationsChecked === 1 ? '' : 's'} checked
              </Typography>
              {LIVE_STATUS_ORDER.map((status) =>
                result.liveStatusCounts?.[status] ? (
                  <Chip
                    key={status}
                    size="small"
                    color={LIVE_STATUS_COLOR[status]}
                    variant={
                      status === 'orphaned' || status === 'stale'
                        ? 'filled'
                        : 'outlined'
                    }
                    label={`${status}: ${result.liveStatusCounts[status]}`}
                  />
                ) : null,
              )}
              {Object.entries(result.providerCounts || {}).map(
                ([provider, count]) => (
                  <Chip
                    key={provider}
                    size="small"
                    variant="outlined"
                    label={`${provider}: ${count}`}
                  />
                ),
              )}
            </>
          ) : null}
        </Box>
        <Button
          size="small"
          startIcon={<Refresh fontSize="small" />}
          onClick={runCheck}
          disabled={fetching}
        >
          Refresh
        </Button>
      </Box>
      {error ? (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      ) : null}
    </Paper>
  );
};

export default SimulationHealthCard;
